import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Directive({
  selector: '[rdrxTruncate]',
  standalone: true,
})
export class TruncateDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input('rdrxTruncate') truncateLimit;
  // TODO check this after ng15 migration
  /* eslint-disable @angular-eslint/no-output-rename */
  @Input() isEditMode = false;
  @Output('text') text: EventEmitter<string> = new EventEmitter<string>();
  @HostBinding('attr.role') role = 'alert';
  @HostBinding('attr.aria-atomic') atomic = 'true';

  private originalInput: string;
  private truncatedInput: string;
  private truncated = false;
  private showMoreLink!: HTMLAnchorElement;
  private instanceId!: string;
  private readonly unlisteners: Array<() => void> = [];

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly liveAnnouncer: LiveAnnouncer,
  ) {}

  ngAfterViewInit(): void {
    const element: HTMLElement = this.el.nativeElement;
    this.instanceId = element.id;

    this.originalInput = element.innerHTML;
    this.truncatedInput = this.originalInput.slice(
      0,
      this.truncateLimit || 100,
    );

    if (this.originalInput !== this.truncatedInput) {
      this.setupShowMoreLink();
      this.toggleTruncated();
      element.insertAdjacentElement('afterend', this.showMoreLink);
    } else {
      this.text.emit(this.originalInput);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If isEditMode changes after init, update the link's state
    if (
      changes.isEditMode &&
      this.showMoreLink &&
      this.originalInput !== this.truncatedInput
    ) {
      this.updateShowMoreLinkDisabledState();
    }
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach((u) => u());
  }

  /** returns exactly what’s visible right now */
  private getVisibleText(): string {
    return this.truncated
      ? `${this.truncatedInput}…`
      : this.originalInput;
  }

  private setupShowMoreLink(): void {
    this.showMoreLink = this.renderer.createElement('a');
    this.renderer.setAttribute(this.showMoreLink, 'href', 'javascript:void(0)');

    // aria-controls & expanded
    if (this.instanceId) {
      this.renderer.setAttribute(this.showMoreLink, 'aria-controls', this.instanceId);
    }
    this.renderer.setAttribute(
      this.showMoreLink,
      'aria-expanded',
      this.truncated ? 'true' : 'false',
    );

    // styling
    this.renderer.setStyle(this.showMoreLink, 'display', 'block');
    this.renderer.addClass(this.showMoreLink, 'ahe-ui-link');
    this.renderer.addClass(this.showMoreLink, 'rdrx-truncate-toggle');

    // click listener
    this.unlisteners.push(
      this.renderer.listen(this.showMoreLink, 'click', (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        this.ngZone.run(() => this.toggleTruncated());
      }),
    );

    // keyboard listener
    this.unlisteners.push(
      this.renderer.listen(this.showMoreLink, 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          this.ngZone.run(() => this.toggleTruncated());
        }
      }),
    );

    this.updateShowMoreLinkDisabledState();
  }

  private updateShowMoreLinkDisabledState(): void {
    if (!this.showMoreLink) return;
    if (this.isEditMode) {
      this.renderer.setAttribute(this.showMoreLink, 'aria-disabled', 'true');
      this.renderer.addClass(this.showMoreLink, 'disabled');
      this.renderer.setAttribute(this.showMoreLink, 'tabindex', '-1');
      this.renderer.setStyle(this.showMoreLink, 'pointer-events', 'none');
      this.renderer.setStyle(this.showMoreLink, 'color', '#999');
      this.renderer.setAttribute(this.showMoreLink, 'title', 'Disabled in edit mode');
    } else {
      this.renderer.removeAttribute(this.showMoreLink, 'aria-disabled');
      this.renderer.removeClass(this.showMoreLink, 'disabled');
      this.renderer.setAttribute(this.showMoreLink, 'tabindex', '0');
      this.renderer.setStyle(this.showMoreLink, 'pointer-events', 'auto');
      this.renderer.setStyle(this.showMoreLink, 'color', null);
      this.renderer.removeAttribute(this.showMoreLink, 'title');
    }
  }

  toggleTruncated(): void {
    // Flip the state and get the new visible text
    this.truncated = !this.truncated;
    const message = this.getVisibleText();

    // Clear any pending CDK announcements
    void this.liveAnnouncer.clear();

    // Remove the existing hidden live-announcer container
    const oldEl = document.getElementById('cdk-live-announcer-element');
    oldEl?.parentNode?.removeChild(oldEl);

    // Update host element’s text
    this.renderer.setProperty(
      this.el.nativeElement,
      'innerText',
      message,
    );

    // Re-announce assertively — CDK will re-create the live-region element
    void this.liveAnnouncer.announce(message, 'assertive');

    // Update the “Show More/Less” link
    const linkText = this.truncated ? 'Show More' : 'Show Less';
    this.renderer.setProperty(this.showMoreLink, 'innerText', linkText);
    this.renderer.setAttribute(
      this.showMoreLink,
      'aria-expanded',
      this.truncated ? 'false' : 'true',
    );
    this.renderer.setAttribute(
      this.showMoreLink,
      'aria-label',
      `${linkText}`,
    );

    this.updateShowMoreLinkDisabledState();

    // Emit the new text
    this.text.emit(message);
  }
}
